/**
 * Hygienist Pay Calculator Service
 * 
 * Calculates hygienist contribution to provider pay for CDP reports.
 * Follows the formula: hygBillableCare × (payOverBase / 100)
 * 
 * @module services/hygienistPayCalculator
 */

/**
 * Calculate hygienist contribution to provider pay.
 * Formula: hygBillableCare × (payOverBase / 100)
 * 
 * Only calculates if hygBillableCare > 0, otherwise returns 0.
 * 
 * @param {number} hygBillableCare - Hygienist billable care amount
 * @param {number} payOverBase - Pay over base percentage
 * @returns {number} Calculated hygienist pay contribution (0 if hygBillableCare <= 0)
 * 
 * @example
 * calculateHygienistPayContribution(1000, 30) // Returns 300
 * calculateHygienistPayContribution(0, 30)    // Returns 0
 * calculateHygienistPayContribution(-100, 30) // Returns 0
 */
export const calculateHygienistPayContribution = (hygBillableCare, payOverBase) => {
    const hygCare = parseFloat(hygBillableCare) || 0;
    const payRate = parseFloat(payOverBase) || 0;
    
    // Only calculate if hygBillableCare > 0
    if (hygCare <= 0) {
        return 0;
    }
    
    return hygCare * (payRate / 100);
};

/**
 * Extract payOverBase value from provider row data.
 * Checks multiple possible data sources in order of precedence.
 * 
 * @param {object} row - Provider row data
 * @returns {number} Pay over base percentage (0 if not found)
 * 
 * @private
 */
const extractPayOverBase = (row) => {
    return row.doctorPayVariables?.payOverBase 
        || row.payOverBase 
        || row.doctorPayVariables?.someValues?.Doctors?.[0]?.payOverBase 
        || 0;
};

/**
 * Get hygienist pay data for display in the pay section.
 * Validates hygBillableCare > 0 before proceeding with calculations.
 * 
 * @param {object} row - Provider row data containing doctor variables
 * @param {object} pracHynBillableCareTotal - Practice-wise hygienist care totals keyed by ABBR
 * @returns {object} Display data object
 * @returns {number} returns.hygBillableCare - Total hygienist billable care for provider
 * @returns {number} returns.payOverBase - Pay over base percentage
 * @returns {number} returns.calculatedPay - Calculated hygienist pay contribution
 * @returns {boolean} returns.shouldDisplay - Whether to display the asterisk (true only if hygBillableCare > 0)
 * 
 * @example
 * const displayData = getHygienistPayDisplayData(row, pracHynBillableCareTotal);
 * if (displayData.shouldDisplay) {
 *   // Show asterisk with calculatedPay
 * }
 */
export const getHygienistPayDisplayData = (row, pracHynBillableCareTotal) => {
    const hygBillableCare = parseFloat(pracHynBillableCareTotal[row.ABBR]) || 0;
    
    // Only proceed if hygBillableCare > 0
    if (hygBillableCare <= 0) {
        return {
            hygBillableCare: 0,
            payOverBase: 0,
            calculatedPay: 0,
            shouldDisplay: false
        };
    }
    
    const payOverBase = extractPayOverBase(row);
    const calculatedPay = calculateHygienistPayContribution(hygBillableCare, payOverBase);
    
    return {
        hygBillableCare,
        payOverBase,
        calculatedPay,
        shouldDisplay: calculatedPay > 0
    };
};
